import { useMutation } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { AnchorContent, AnchorContentType } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'

interface Data {
  anchor_content: AnchorContent
}

export interface Variables {
  signal?: AbortSignal
  type: AnchorContentType
  source_url?: string
  url?: string
  mime_type?: string
}

const YOUTUBE_PATTERN = /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)/i

export class YoutubeLinkError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'YoutubeLinkError'
  }
}

export const createAnchorContent = async ({
  type,
  source_url,
  url,
  mime_type,
  workspaceId,
  signal,
}: Variables & { workspaceId: number }) => {
  if (type === 'Website' && url && YOUTUBE_PATTERN.test(url)) {
    throw new YoutubeLinkError('Unable to scrape YouTube')
  }
  const res = await sendToApi<Data>(`/w/${workspaceId}/anchor_contents`, {
    method: 'POST',
    body: {
      type,
      source_url,
      url,
      mime_type,
    },
    signal,
  })

  if (res.type === 'exception' && res.data instanceof DOMException && res.data.name === 'AbortError') {
    throw res.data
  }

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    if (res.data === 'cancel') {
      throw new DOMException('The operation was aborted.', 'AbortError')
    }
    showErrorMessageToast(res.data.errors?.[0]?.detail || 'Something went wrong')
    throw new Error(res.data)
  }

  return res.data
}

export interface Options extends Omit<UseMutationOptions<AnchorContent, Error, Variables | void>, 'mutationFn'> {}

export const useCreateAnchorContent = (options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()

  return useMutation<AnchorContent, Error, Variables>(async (vars) => {
    const result = await createAnchorContent({
      workspaceId,
      ...vars,
    })

    return result.anchor_content
  }, options)
}
